import {
    AlertTriangle,
    Briefcase,
    ChevronLeft,
    CircleUserRound,
    LoaderCircle,
    Sparkles,
    Tag,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './AssignSalaryStructureScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`

const normalizeStaffOptions = (payload) => {
    const candidates = [
        payload?.staff,
        payload?.staffReadiness,
        payload?.staffList,
        payload?.response,
    ]
    const staffArray = candidates.find(Array.isArray) || []

    return staffArray.map((item, index) => ({
        staffIdentifier: item?.staffIdentifier || item?.employeeIdentifier || item?.id || `staff-${index}`,
        staffName: item?.staffName || item?.name || item?.fullName || 'Unnamed Staff',
        designationLabel: item?.designationLabel || item?.designation || 'Staff',
        staffCode: item?.staffCode || item?.employeeCode || '',
    }))
}

const formatComponentValue = (component, basicAmountInput) => {
    const nameOrCode = `${component?.componentName || ''} ${component?.componentCode || ''}`.toLowerCase()
    if (nameOrCode.includes('basic')) {
        return basicAmountInput && Number(basicAmountInput) > 0
            ? formatCurrency(Number(basicAmountInput))
            : '₹[basicAmount entered]'
    }

    if (component?.valueDisplay) return component.valueDisplay

    if (component?.valueType === 'FLAT') return formatCurrency(component?.value)
    if (component?.valueType === 'PERCENT_OF_BASIC') return `${component?.value}% of Basic`
    if (component?.valueType === 'PERCENT_OF_GROSS') return `${component?.value}% of Gross`
    return 'Auto-computed'
}

const AssignSalaryStructureScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const {
        staffIdentifier: prefilledStaffId,
        prefillStaffIdentifier,
        staffName: prefilledStaffName,
        templateIdentifier: prefilledTemplateIdentifier,
        templateName: prefilledTemplateName,
        excludeTemplateIdentifier,
    } = location.state ?? {}

    const [formData, setFormData] = useState({
        staffIdentifier: prefilledStaffId || prefillStaffIdentifier || '',
        templateIdentifier: prefilledTemplateIdentifier || '',
        basicAmount: '',
        effectiveFrom: new Date().toISOString().split('T')[0],
        assignmentRemarks: '',
        revisionReason: '',
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [staffOptions, setStaffOptions] = useState([])
    const [templateOptions, setTemplateOptions] = useState([])
    const [isLoadingStaff, setIsLoadingStaff] = useState(false)
    const [isLoadingTemplates, setIsLoadingTemplates] = useState(false)
    const [isLoadingTemplateDetail, setIsLoadingTemplateDetail] = useState(false)
    const [staffSearch, setStaffSearch] = useState('')

    const storeIdentifier = activeStore?.storeIdentifier || ''

    const setField = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: undefined }))
        }
    }

    const fetchStaffOptions = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingStaff(true)

            const response = await fetch(BASE_URL_API + 'getPayrollStaffReadiness', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()

            if (respo?.status === 'success') {
                setStaffOptions(normalizeStaffOptions(respo?.response || {}))
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch (error) {
            console.error('getPayrollStaffReadiness error:', error)
        } finally {
            setIsLoadingStaff(false)
        }
    }, [authCtx, storeIdentifier])

    const fetchTemplateOptions = useCallback(async () => {
        if (!authCtx?.token || !storeIdentifier) return

        try {
            setIsLoadingTemplates(true)

            const response = await fetch(BASE_URL_API + 'getStoreSalaryStructureList', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    showArchived: false,
                }),
            })

            if (!response.ok) return

            const respo = await response.json()

            if (respo?.status === 'success') {
                const templates = Array.isArray(respo?.response?.templates)
                    ? respo.response.templates
                    : []
                setTemplateOptions(
                    templates.filter((template) => template.templateIdentifier !== excludeTemplateIdentifier)
                )
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
            }
        } catch (error) {
            console.error('getStoreSalaryStructureList error:', error)
        } finally {
            setIsLoadingTemplates(false)
        }
    }, [authCtx, excludeTemplateIdentifier, storeIdentifier])

    const fetchTemplateDetail = useCallback(async (templateIdentifier) => {
        if (!authCtx?.token || !storeIdentifier || !templateIdentifier) return

        try {
            setIsLoadingTemplateDetail(true)

            const response = await fetch(BASE_URL_API + 'getSalaryStructureTemplateDetail', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier,
                    templateIdentifier,
                }),
            })

            if (!response.ok) {
                setSelectedTemplate(null)
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                setSelectedTemplate(respo.response?.template || null)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            setSelectedTemplate(null)
        } catch (error) {
            console.error('getSalaryStructureTemplateDetail error:', error)
            setSelectedTemplate(null)
        } finally {
            setIsLoadingTemplateDetail(false)
        }
    }, [authCtx, storeIdentifier])

    useEffect(() => {
        fetchStaffOptions()
        fetchTemplateOptions()
    }, [fetchStaffOptions, fetchTemplateOptions])

    useEffect(() => {
        if (!formData.templateIdentifier) {
            setSelectedTemplate(null)
            return
        }
        fetchTemplateDetail(formData.templateIdentifier)
    }, [fetchTemplateDetail, formData.templateIdentifier])

    const selectedStaff = useMemo(
        () => staffOptions.find((staff) => staff.staffIdentifier === formData.staffIdentifier) || null,
        [formData.staffIdentifier, staffOptions]
    )

    const filteredStaff = useMemo(() => {
        const q = staffSearch.trim().toLowerCase()
        if (!q) return staffOptions
        return staffOptions.filter((staff) => {
            const hay = `${staff.staffName} ${staff.designationLabel} ${staff.staffCode}`.toLowerCase()
            return hay.includes(q)
        })
    }, [staffOptions, staffSearch])

    const previewComponents = useMemo(
        () => Array.isArray(selectedTemplate?.components?.active) ? selectedTemplate.components.active : [],
        [selectedTemplate]
    )

    const assignSalaryStructure = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        const newErrors = {}
        if (!formData.staffIdentifier) newErrors.staffIdentifier = 'Select a staff member.'
        if (!formData.templateIdentifier) newErrors.templateIdentifier = 'Select a salary template.'
        if (!formData.basicAmount || Number(formData.basicAmount) <= 0) {
            newErrors.basicAmount = 'Enter a valid basic salary amount.'
        }
        if (!formData.effectiveFrom) newErrors.effectiveFrom = 'Select an effective date.'
        if (!formData.assignmentRemarks.trim()) {
            newErrors.assignmentRemarks = 'Provide a reason for this assignment.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'assignStaffSalaryStructure', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    staffIdentifier: formData.staffIdentifier,
                    templateIdentifier: formData.templateIdentifier,
                    basicAmount: parseFloat(formData.basicAmount),
                    effectiveFrom: formData.effectiveFrom,
                    assignedBy: authCtx?.authIdentifier || '',
                    assignmentRemarks: formData.assignmentRemarks.trim(),
                    revisionReason: formData.revisionReason.trim(),
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const { revisionType, staffName, templateName } = respo.response || {}
                const toastMsg = revisionType === 'JOINING'
                    ? `${staffName} assigned to "${templateName}".`
                    : `${staffName}'s salary updated to "${templateName}".`

                toast.success(toastMsg)
                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    if ((respo?.message || '').includes('basicAmount')) {
                        setErrors((prev) => ({ ...prev, basicAmount: respo.message }))
                    } else if ((respo?.message || '').includes('assignmentRemarks')) {
                        setErrors((prev) => ({ ...prev, assignmentRemarks: respo.message }))
                    } else {
                        toast.error(respo?.message || 'Missing required values.')
                    }
                    break

                case 'VAL_005':
                    setErrors((prev) => ({ ...prev, effectiveFrom: 'Invalid date.' }))
                    break

                case 'VAL_006':
                    setErrors((prev) => ({
                        ...prev,
                        effectiveFrom: `Must be after ${respo?.response?.currentAssignmentEffectiveFrom}.`,
                    }))
                    break

                case 'VAL_007':
                    toast.error('Staff member not found.')
                    break

                case 'COMP_004':
                    setErrors((prev) => ({ ...prev, templateIdentifier: 'Template not found.' }))
                    break

                case 'VAL_008':
                    setErrors((prev) => ({
                        ...prev,
                        templateIdentifier: `"${respo?.response?.templateName}" is archived. Select a different template.`,
                    }))
                    break

                case 'SYS_002':
                    toast.error(respo?.message || 'Assignment failed due to transaction rollback.')
                    break

                default:
                    toast.error(respo?.message || 'Failed to assign salary. Please try again.')
                    break
            }
        } catch (error) {
            console.error('assignStaffSalaryStructure error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Payroll</span>
                    <h1 className={styles.headerTitle}>Assign Salary Structure</h1>
                    {(prefilledStaffName || selectedStaff?.staffName) && (
                        <p className={styles.headerSub}>for {prefilledStaffName || selectedStaff?.staffName}</p>
                    )}
                </div>
            </header>

            <div className={styles.formWrap}>
                {!storeIdentifier && (
                    <section className={styles.alertCard}>
                        <AlertTriangle size={14} />
                        <div>
                            <strong>Select an active store to continue.</strong>
                            <p>Salary assignment is store-specific.</p>
                        </div>
                    </section>
                )}

                {!!formData.staffIdentifier && (
                    <section className={styles.readonlyStaffCard}>
                        <CircleUserRound size={16} />
                        <div>
                            <p className={styles.readonlyLabel}>Staff Member</p>
                            <p className={styles.readonlyValue}>{prefilledStaffName || selectedStaff?.staffName || formData.staffIdentifier}</p>
                        </div>
                    </section>
                )}

                {!formData.staffIdentifier && (
                    <section className={styles.formCard}>
                        <label className={styles.fieldLabel} htmlFor="staffSearch">Staff Member <span className={styles.required}>*</span></label>
                        <input
                            id="staffSearch"
                            className={styles.textInput}
                            type="text"
                            placeholder={isLoadingStaff ? 'Loading staff…' : 'Search by name / code'}
                            value={staffSearch}
                            onChange={(event) => setStaffSearch(event.target.value)}
                        />

                        <div className={styles.pickerList}>
                            {filteredStaff.map((staff) => (
                                <button
                                    type="button"
                                    key={staff.staffIdentifier}
                                    className={`${styles.pickerItem} ${formData.staffIdentifier === staff.staffIdentifier ? styles.pickerItemActive : ''}`}
                                    onClick={() => setField('staffIdentifier', staff.staffIdentifier)}
                                >
                                    <span className={styles.pickerName}>{staff.staffName}</span>
                                    <span className={styles.pickerMeta}>{staff.designationLabel}{staff.staffCode ? ` · ${staff.staffCode}` : ''}</span>
                                </button>
                            ))}
                            {!isLoadingStaff && filteredStaff.length === 0 && (
                                <p className={styles.emptyText}>No staff found.</p>
                            )}
                        </div>
                        {errors.staffIdentifier && <p className={styles.fieldError}>{errors.staffIdentifier}</p>}
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="templateIdentifier">Salary Structure <span className={styles.required}>*</span></label>
                    <select
                        id="templateIdentifier"
                        className={`${styles.selectInput} ${errors.templateIdentifier ? styles.inputError : ''}`}
                        value={formData.templateIdentifier}
                        onChange={(event) => setField('templateIdentifier', event.target.value)}
                    >
                        <option value="">{isLoadingTemplates ? 'Loading salary structures…' : 'Select a salary structure'}</option>
                        {templateOptions.map((template) => (
                            <option key={template.templateIdentifier} value={template.templateIdentifier}>
                                {template.templateName}
                                {template.designationLabel ? ` — ${template.designationLabel}` : ''}
                            </option>
                        ))}
                    </select>
                    {prefilledTemplateIdentifier && (
                        <p className={styles.fieldHint}>Prefilled from: {prefilledTemplateName || 'Salary Structure Detail'}</p>
                    )}
                    {errors.templateIdentifier && <p className={styles.fieldError}>{errors.templateIdentifier}</p>}

                    {formData.templateIdentifier && (
                        <div className={styles.templateQuickMeta}>
                            {templateOptions
                                .filter((item) => item.templateIdentifier === formData.templateIdentifier)
                                .map((item) => (
                                    <span key={item.templateIdentifier} className={styles.metaChip}>
                                        <Tag size={11} /> {item.designationLabel || 'General'}
                                    </span>
                                ))}
                        </div>
                    )}
                </section>

                {!!formData.templateIdentifier && (
                    <section className={styles.previewCard}>
                        <div className={styles.previewHeader}>
                            <Briefcase size={14} /> Template Preview
                            {isLoadingTemplateDetail && <LoaderCircle size={13} className={styles.spin} />}
                        </div>
                        {!isLoadingTemplateDetail && previewComponents.length === 0 && (
                            <p className={styles.emptyText}>No active components found for this template.</p>
                        )}
                        {!isLoadingTemplateDetail && previewComponents.length > 0 && (
                            <div className={styles.previewList}>
                                {previewComponents.map((component) => (
                                    <div key={component.componentIdentifier} className={styles.previewRow}>
                                        <span className={styles.previewName}>{component.componentName}</span>
                                        <span className={styles.previewValue}>{formatComponentValue(component, formData.basicAmount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>
                )}

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="basicAmount">Basic Salary (₹) <span className={styles.required}>*</span></label>
                    <input
                        id="basicAmount"
                        className={`${styles.textInput} ${errors.basicAmount ? styles.inputError : ''}`}
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.basicAmount}
                        onChange={(event) => setField('basicAmount', event.target.value)}
                        placeholder="e.g. 14000"
                    />
                    <p className={styles.fieldHint}>Other components (HRA, PF etc.) are calculated from this amount.</p>
                    {errors.basicAmount && <p className={styles.fieldError}>{errors.basicAmount}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="effectiveFrom">Effective From <span className={styles.required}>*</span></label>
                    <input
                        id="effectiveFrom"
                        className={`${styles.textInput} ${errors.effectiveFrom ? styles.inputError : ''}`}
                        type="date"
                        value={formData.effectiveFrom}
                        onChange={(event) => setField('effectiveFrom', event.target.value)}
                    />
                    {errors.effectiveFrom && <p className={styles.fieldError}>{errors.effectiveFrom}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="assignmentRemarks">Assignment Note <span className={styles.required}>*</span></label>
                    <textarea
                        id="assignmentRemarks"
                        className={`${styles.textArea} ${errors.assignmentRemarks ? styles.inputError : ''}`}
                        rows={3}
                        value={formData.assignmentRemarks}
                        onChange={(event) => setField('assignmentRemarks', event.target.value)}
                        placeholder="e.g. Joining salary, Annual increment, Promotion"
                    />
                    {errors.assignmentRemarks && <p className={styles.fieldError}>{errors.assignmentRemarks}</p>}
                </section>

                <section className={styles.formCard}>
                    <label className={styles.fieldLabel} htmlFor="revisionReason">Detailed Reason (for records)</label>
                    <textarea
                        id="revisionReason"
                        className={styles.textArea}
                        rows={3}
                        value={formData.revisionReason}
                        onChange={(event) => setField('revisionReason', event.target.value)}
                        placeholder="e.g. Annual performance review — rating 4/5"
                    />
                </section>

                <button
                    type="button"
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    onClick={assignSalaryStructure}
                    disabled={isSaving || !storeIdentifier}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                        : 'Assign Salary Structure →'}
                </button>
            </div>
        </div>
    )
}

export default AssignSalaryStructureScreen